import { faClose } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { createPortal } from 'react-dom'
import { Button, type ButtonProps } from '../button'
import type { ReactNode } from 'react'
import { useState, useEffect, useRef } from 'react'
import { useTimeout } from '@mantine/hooks'

const TRANSITION_DURATION = 300

enum TransitionState {
  HIDDEN = 'HIDDEN',
  BEFORE_ENTER = 'BEFORE_ENTER',
  ENTER = 'ENTER',
  VISIBLE = 'VISIBLE',
  EXIT = 'EXIT',
  AFTER_EXIT = 'AFTER_EXIT',
}

type ModalButton = Pick<
  ButtonProps,
  'text' | 'onClick' | 'colour' | 'className' | 'css' | 'icon' | 'disabled'
>

interface ModalWrapperProps {
  // Supports legacy [state, setState], new managed state props, or raw boolean
  open?: [boolean, (open: boolean) => void] | boolean
  setOpen?: (open: boolean) => void
  title?: string
  subtitle?: string
  children?: ReactNode
  width?: number
  buttons?: ModalButton[]
  closeable?: boolean
  onShow?: () => void
  onHide?: () => void
  working?: boolean
  className?: string
  bodyClass?: string
  dataLabel?: string
}

function Portal({ children }: { children: ReactNode }) {
  const modalRoot = document.getElementById('modal-root')
  if (!modalRoot) {
    console.error('Modal root element not found')
    return null
  }
  return createPortal(children, modalRoot)
}

export default function ModalWrapper({
  open,
  setOpen,
  title = '',
  subtitle = '',
  children,
  width = 800,
  buttons = [],
  closeable = true,
  onShow,
  onHide,
  working = false,
  className = '',
  bodyClass = '',
  dataLabel = '',
  ...props
}: ModalWrapperProps) {
  const modalChildRef = useRef<HTMLElement>(null)
  const [state, setState] = useState<TransitionState>(TransitionState.HIDDEN)

  // Store callbacks in refs to avoid dependency issues
  const onShowRef = useRef(onShow)
  const onHideRef = useRef(onHide)

  useEffect(() => {
    onShowRef.current = onShow
    onHideRef.current = onHide
  })

  // Auto-detect which mode we're in
  let isOpen: boolean
  let setIsOpen: (value: boolean) => void

  if (Array.isArray(open)) {
    // Legacy mode: open = [isOpen, setIsOpen]
    ;[isOpen, setIsOpen] = open
  } else {
    // New managed mode: open = boolean, setOpen = function
    isOpen = open ?? false
    setIsOpen = setOpen ?? (() => {})
  }

  // Opening animation timeouts
  const { start: startVisible, clear: clearVisible } = useTimeout(
    () => setState(TransitionState.VISIBLE),
    50,
  )

  // Closing animation timeouts
  const { start: startAfterExit, clear: clearAfterExit } = useTimeout(
    () => setState(TransitionState.AFTER_EXIT),
    TRANSITION_DURATION,
  )
  const { start: startHidden, clear: clearHidden } = useTimeout(
    () => setState(TransitionState.HIDDEN),
    50,
  )

  useEffect(() => {
    if (isOpen) {
      onShowRef.current?.()
      setState(TransitionState.BEFORE_ENTER)
      startVisible()

      return () => {
        clearVisible()
      }
    } else {
      onHideRef.current?.()
      setState(TransitionState.EXIT)
      startAfterExit()
      // Chain the hidden timeout after afterExit
      const afterExitTimer = setTimeout(() => {
        startHidden()
      }, TRANSITION_DURATION)

      return () => {
        clearTimeout(afterExitTimer)
        clearAfterExit()
        clearHidden()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleOnClose = () => {
    setIsOpen(false)
  }

  const handleOutsideClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (!closeable) return
    if (modalChildRef.current?.contains(event.target as Node)) return
    handleOnClose()
  }

  if (state === TransitionState.HIDDEN) return null

  const isExiting = [
    TransitionState.HIDDEN,
    TransitionState.BEFORE_ENTER,
    TransitionState.EXIT,
    TransitionState.AFTER_EXIT,
  ].includes(state)

  const isEntering = [TransitionState.ENTER, TransitionState.VISIBLE].includes(
    state,
  )

  return (
    <Portal>
      <div
        className="fixed inset-0 z-1000"
        onMouseDown={handleOutsideClick}
      >
        {/* Background */}
        <div
          className={[
            'fixed z-1001 inset-0 bg-black/50 transition-opacity duration-300 ease-out backdrop-blur-sm',
            isExiting ? 'opacity-0' : '',
            isEntering ? 'opacity-100' : '',
          ].join(' ')}
        />

        {/* Modal */}
        <div className="fixed z-1002 inset-0 overflow-y-auto">
          <div
            style={{ width: `${width}px` }}
            className={[
              'max-w-[90vw] transition duration-300 ease-out mx-auto my-12',
              isExiting ? 'opacity-0 scale-95' : '',
              isEntering ? 'opacity-100 scale-100' : '',
              className,
            ].join(' ')}
            data-label={dataLabel}
            {...props}
          >
            <section
              ref={modalChildRef}
              className="bg-gray-900 rounded-sm"
            >
              <header className="flex justify-between items-center px-6 py-3.5">
                <div>
                  <h2 className="text-2xl font-semibold">{title}</h2>
                  {subtitle && (
                    <p className="text-xs pt-2 text-gray-300">{subtitle}</p>
                  )}
                </div>
                {closeable && (
                  <button
                    className="text-gray-500 hover:text-white w-9 h-9 -mr-3"
                    onClick={handleOnClose}
                  >
                    <FontAwesomeIcon icon={faClose} />
                  </button>
                )}
              </header>
              <div className={['px-6 pb-6', bodyClass].join(' ')}>
                {children}
              </div>
              {buttons.length > 0 && (
                <footer className="flex items-center justify-center gap-3 px-6 pb-6">
                  {buttons.map((button, i) => (
                    <Button
                      key={`modal-button-${i}`}
                      text={button.text}
                      onClick={button.onClick}
                      className={[
                        button.colour === 'dark' ? 'bg-gray-800!' : '',
                        button.className,
                        button.css,
                      ].join(' ')}
                      icon={button.icon}
                      loading={working}
                      colour={button.colour}
                      disabled={button.disabled}
                    />
                  ))}
                </footer>
              )}
            </section>
          </div>
        </div>
      </div>
    </Portal>
  )
}
